import { useState, useEffect, useCallback, useRef, Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { Card } from '@/types'
import { collectionApi, collectionsApi, cardtraderApi, cardsApi, feedbackApi, storeApi, api } from '@/services/api'
import toast from 'react-hot-toast'
import { formatCurrency } from '@/lib/formatters'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import CardImage from '@/components/common/CardImage'
import { useCardHistoryStore } from '@/stores/cardHistoryStore'
import { decodeHtmlEntities } from '@/lib/htmlDecode'
import { baseCardId } from '@/lib/cardId'
import {
  ChevronDown,
  ChevronUp,
  Plus,
  Minus,
  X,
  Sparkles,
  Swords,
  Shield,
  Heart,
  Users,
  Zap,
  AlertTriangle,
  Palette,
  Tag,
  Layers,
  Crown,
  Star,
  FolderPlus,
  TrendingUp,
  TrendingDown,
  ExternalLink,
  RefreshCw,
  Loader2,
  MessageSquareWarning,
  Send,
  Pencil,
  Check,
  Lock,
  Store,
  Trash2
} from 'lucide-react'
import { proxyImageUrl } from '@/utils/imageLoader'
import { useAuth } from '@/contexts/AuthContext'
import AdminCardEditModal from '@/components/admin/AdminCardEditModal'
import ErrataPairView from '@/components/admin/ErrataPairView'

interface CardDetailModalProps {
  card: Card | null
  isOpen: boolean
  onClose: () => void
  quantity: number
  collectionId?: number
  onCardChange?: (card: Card) => void
  selectedCollectionId?: string | number | null
  inline?: boolean // Render as full page instead of modal
  skipHistory?: boolean // Don't log viewed/updated events to history
}

function formatScanAge(dateStr: string): string {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000)
  if (diff < 0 || isNaN(diff)) return 'unknown'
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return `${Math.floor(diff / 86400)}d ago`
}

export default function CardDetailModal({
  card,
  isOpen,
  onClose,
  quantity: initialQuantity,
  collectionId,
  onCardChange,
  selectedCollectionId,
  inline = false,
  skipHistory = false
}: CardDetailModalProps) {
  const [quantity, setQuantity] = useState(initialQuantity || 0)
  const [saveTimeout, setSaveTimeout] = useState<NodeJS.Timeout | null>(null)
  const [variantsExpanded, setVariantsExpanded] = useState(false)
  const [variants, setVariants] = useState<Card[]>([])
  const [loadingVariants, setLoadingVariants] = useState(false)
  const [userExpandedVariants, setUserExpandedVariants] = useState(false)
  const [inputValue, setInputValue] = useState(String(initialQuantity || 0))
  const [currentCollectionId, setCurrentCollectionId] = useState(selectedCollectionId)
  const [showCollectionMenu, setShowCollectionMenu] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [priceExpanded, setPriceExpanded] = useState(true)
  const [newCollectionName, setNewCollectionName] = useState('')
  const [newCollectionDescription, setNewCollectionDescription] = useState('')
  const [listingsExpanded, setListingsExpanded] = useState(false)
  const [editingCmUrl, setEditingCmUrl] = useState(false)
  const [cmUrlInput, setCmUrlInput] = useState('')
  const [showFeedback, setShowFeedback] = useState(false)
  const [feedbackCategory, setFeedbackCategory] = useState('other')
  const [feedbackMessage, setFeedbackMessage] = useState('')
  const [feedbackSending, setFeedbackSending] = useState(false)
  const [storeExpanded, setStoreExpanded] = useState(false)
  const [newStoreCondition, setNewStoreCondition] = useState('NM')
  const [newStorePrice, setNewStorePrice] = useState('')
  const [newStoreQty, setNewStoreQty] = useState('1')
  const [newStoreNfs, setNewStoreNfs] = useState(false)
  const [adminEditOpen, setAdminEditOpen] = useState(false)
  const [errataPairOpen, setErrataPairOpen] = useState(false)
  const { user } = useAuth()
  const isAdmin = !!user?.is_admin
  const queryClient = useQueryClient()
  const isLeader = card?.type === 'LEADER'
  const previousQtyRef = useRef<number>(0)
  const addHistoryEntry = useCardHistoryStore((s) => s.addEntry)

  // Track card viewed event
  useEffect(() => {
    if (isOpen && card && !skipHistory) {
      addHistoryEntry({
        cardId: card.id,
        cardName: card.name,
        cardImageUrl: card.image_url,
        setCode: card.set_id,
        action: 'viewed',
      })
    }
  }, [isOpen, card?.id])

  // Track previous quantity for history
  useEffect(() => {
    previousQtyRef.current = initialQuantity || 0
  }, [card?.id, initialQuantity])

  // Fetch user's collections
  const { data: userCollections = [] } = useQuery({
    queryKey: ['collections'],
    queryFn: collectionsApi.getAll,
  })

  // Fetch card quantities across all collections
  const { data: collectionQuantities = [] } = useQuery({
    queryKey: ['card-quantities', card?.id],
    queryFn: async () => {
      if (!card) return []
      const quantities = await Promise.all(
        userCollections.map(async (collection: any) => {
          const items = await collectionApi.getAll(collection.id)
          const item = items.find((i: any) => i.card_id === card.id)
          return {
            collectionId: collection.id,
            collectionName: collection.name,
            quantity: item?.quantity || 0,
            foilQuantity: item?.foil_quantity || 0
          }
        })
      )
      return quantities
    },
    enabled: !!card && userCollections.length > 0,
  })

  // Fetch card price (lazy - only when expanded)
  const { data: priceData, isLoading: priceLoading } = useQuery({
    queryKey: ['card-price', card?.id],
    queryFn: () => cardtraderApi.getCardPrice(card!.id),
    enabled: !!card && priceExpanded,
    staleTime: 1000 * 60 * 30, // 30 min
    retry: false,
  })

  // Fetch price history (lazy - only when price section expanded and price data available)
  const { data: historyData } = useQuery({
    queryKey: ['card-price-history', card?.id],
    queryFn: () => cardtraderApi.getCardPriceHistory(card!.id, 30),
    enabled: !!card && priceExpanded && !!priceData?.price,
    staleTime: 1000 * 60 * 30,
    retry: false,
  })

  // Fetch CM listings (lazy - only when expanded)
  const { data: cmListingsData } = useQuery({
    queryKey: ['cm-listings', card?.id],
    queryFn: async () => {
      const { data } = await api.get(`/api/cards/${card!.id}/cardmarket-listings`)
      return data as { listings: { s: string; p: number; q: number; c: string | null; l: string | null; sh: number | null; d: string | null }[]; total: number; scraped_at: string | null }
    },
    enabled: !!card && listingsExpanded,
    staleTime: 1000 * 60 * 15,
    retry: false,
  })

  // Fetch all prices (cached) for collection breakdown values
  const { data: allPrices = {} } = useQuery({
    queryKey: ['all-card-prices'],
    queryFn: cardtraderApi.getAllPrices,
    staleTime: 5 * 60 * 1000,
  })

  // Fetch CT inventory listing prices (cached)
  const { data: ctInventoryPrices = {} } = useQuery({
    queryKey: ['ct-inventory-prices'],
    queryFn: cardtraderApi.getInventoryPrices,
    staleTime: 5 * 60 * 1000,
  })

  const cardPrice = card && allPrices[card.id] ? allPrices[card.id].min / 100 : undefined
  const ctListingPrice = card && ctInventoryPrices[card.id] != null ? ctInventoryPrices[card.id] / 100 : undefined

  // Cross-language counterpart price
  const isCurrentJP = card?.language === 'ja' || card?.id?.endsWith('-JP') || card?.id?.includes('-JP_')
  const counterpartCardId = card?.id
    ? isCurrentJP
      ? card.id.replace('-JP', '') // OP01-001-JP -> OP01-001, OP01-001-JP_p1 -> OP01-001_p1
      : card.id.replace(/(_[a-z]\d+)?$/, '-JP$1') // OP01-001 -> OP01-001-JP, OP01-001_p1 -> OP01-001-JP_p1
    : null
  const counterpartLabel = isCurrentJP ? 'EN' : 'JP'
  const counterpartFlag = isCurrentJP ? '\u{1F1FA}\u{1F1F8}' : '\u{1F1EF}\u{1F1F5}'

  // Fetch counterpart price (lazy - only when price section expanded)
  const { data: counterpartPriceData } = useQuery({
    queryKey: ['card-price', counterpartCardId],
    queryFn: () => cardtraderApi.getCardPrice(counterpartCardId!),
    enabled: !!counterpartCardId && priceExpanded,
    staleTime: 1000 * 60 * 30,
    retry: false,
  })

  // Sync single card price
  const syncPriceMutation = useMutation({
    mutationFn: (cardId: string) => cardtraderApi.syncCardPrice(cardId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['card-price', card?.id] })
      queryClient.invalidateQueries({ queryKey: ['card-price-history', card?.id] })
    },
  })

  // Store listings for this card — server-side filter by card_id so we don't
  // pull every listing in the store just to show the few rows for one card.
  const { data: cardStoreListings = [] } = useQuery({
    queryKey: ['store-card-listings', card?.id],
    queryFn: async () => {
      if (!card?.id) return []
      return storeApi.getListings({ card_id: card.id, status: 'any', limit: 100 })
    },
    enabled: !!card && storeExpanded,
    staleTime: 10 * 1000,
  })

  const createStoreMutation = useMutation({
    mutationFn: storeApi.createListing,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-card-listings', card?.id] })
      queryClient.invalidateQueries({ queryKey: ['store-my-listings'] })
      toast.success('Added to store')
      setNewStorePrice('')
      setNewStoreQty('1')
      setNewStoreNfs(false)
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to add listing'),
  })

  const deleteStoreMutation = useMutation({
    mutationFn: storeApi.deleteListing,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-card-listings', card?.id] })
      queryClient.invalidateQueries({ queryKey: ['store-my-listings'] })
      toast.success('Removed from store')
    },
  })

  // Calculate total owned across all collections
  const totalOwned = collectionQuantities.reduce((sum: number, cq: any) => sum + cq.quantity + cq.foilQuantity, 0)

  // Get current collection name and read-only status
  const currentCollection = userCollections.find((c: any) => c.id === currentCollectionId)
  const currentCollectionName = currentCollection?.name || 'Select Collection'
  const isReadOnlyCollection = currentCollectionId === 'merged' || currentCollection?.collection_type === 'tcgpowertools'
  
  // Set default collection if none selected or if merged view
  useEffect(() => {
    if ((!currentCollectionId || currentCollectionId === 'merged') && userCollections.length > 0) {
      const defaultCollection = userCollections.find((c: any) => c.is_default) || userCollections[0]
      if (defaultCollection) {
        setCurrentCollectionId(defaultCollection.id)
      }
    }
  }, [userCollections, currentCollectionId])

  useEffect(() => {
    // If we have collection quantities loaded, use the quantity for the current collection
    if (collectionQuantities.length > 0 && currentCollectionId) {
      const collectionData = collectionQuantities.find((cq: any) => cq.collectionId === currentCollectionId)
      const collectionQuantity = collectionData?.quantity || 0
      setQuantity(collectionQuantity)
      setInputValue(String(collectionQuantity))
    } else {
      // Otherwise use the initial quantity passed as prop
      setQuantity(initialQuantity || 0)
      setInputValue(String(initialQuantity || 0))
    }
  }, [initialQuantity, collectionQuantities, currentCollectionId])
  
  // Separate effect for fetching variants - only re-fetch when the base card changes
  // (not when switching between variants of the same card)
  const cardBaseId = baseCardId(card?.id)
  useEffect(() => {
    if (cardBaseId && isOpen) {
      fetchCardVariants()
    }
  }, [cardBaseId, isOpen])

  // Reset variants expansion state only when modal opens/closes
  useEffect(() => {
    if (inline) {
      // In page mode, always show variants and listings expanded
      setVariantsExpanded(true)
      setListingsExpanded(true)
      return
    }
    if (isOpen) {
      // Modal is opening - reset to collapsed unless user had expanded it
      if (!userExpandedVariants) {
        setVariantsExpanded(false)
      }
    } else {
      // Modal is closing - reset user interaction tracking
      setUserExpandedVariants(false)
      setVariantsExpanded(false)
    }
  }, [isOpen, inline])

  const fetchCardVariants = async () => {
    if (!card) return
    
    setLoadingVariants(true)
    try {
      const response = await api.get('/api/cards/variants', {
        params: { baseId: card.id }
      })
      // Remove duplicates just in case
      const uniqueVariants = response.data?.filter((variant: any, index: number, array: any[]) => 
        array.findIndex(v => v.id === variant.id) === index
      ) || []
      setVariants(uniqueVariants)
    } catch (error) {
      console.error('Error fetching variants:', error)
      setVariants([])
    } finally {
      setLoadingVariants(false)
    }
  }

  // Create collection mutation
  const createCollectionMutation = useMutation({
    mutationFn: ({ name, description }: { name: string; description?: string }) =>
      collectionsApi.create(name, description),
    onSuccess: (newCollection) => {
      queryClient.invalidateQueries({ queryKey: ['collections'] })
      setCurrentCollectionId(newCollection.id)
      setShowCreateModal(false)
      setNewCollectionName('')
      setNewCollectionDescription('')
    },
  })

  const updateMutation = useMutation({
    mutationFn: async (qty: number) => {
      if (!card || !currentCollectionId) return
      
      // Find if we have an existing entry for this card in this collection
      const existingItems = await collectionApi.getAll(currentCollectionId)
      const existingItem = existingItems.find((item: any) => item.card_id === card.id)
      
      if (qty === 0 && existingItem) {
        return collectionApi.remove(existingItem.id)
      } else if (existingItem) {
        return collectionApi.update(existingItem.id, qty, 0)
      } else if (qty > 0) {
        return collectionApi.add(card.id, qty, 0, Number(currentCollectionId))
      }
    },
    onSuccess: (_data, qty) => {
      // Log history event
      if (card) {
        const prevQty = previousQtyRef.current
        if (qty > 0 && prevQty === 0) {
          addHistoryEntry({ cardId: card.id, cardName: card.name, cardImageUrl: card.image_url, setCode: card.set_id, cardmarketUrl: card.cardmarket_url, blueprintId: card.blueprint_id, action: 'added', collectionName: currentCollectionName, previousQuantity: 0, newQuantity: qty })
        } else if (qty === 0 && prevQty > 0) {
          addHistoryEntry({ cardId: card.id, cardName: card.name, cardImageUrl: card.image_url, setCode: card.set_id, cardmarketUrl: card.cardmarket_url, blueprintId: card.blueprint_id, action: 'removed', collectionName: currentCollectionName, previousQuantity: prevQty, newQuantity: 0 })
        } else if (qty !== prevQty) {
          addHistoryEntry({ cardId: card.id, cardName: card.name, cardImageUrl: card.image_url, setCode: card.set_id, cardmarketUrl: card.cardmarket_url, blueprintId: card.blueprint_id, action: 'updated', collectionName: currentCollectionName, previousQuantity: prevQty, newQuantity: qty })
        }
        previousQtyRef.current = qty
      }
      queryClient.invalidateQueries({ queryKey: ['collection'] })
      queryClient.invalidateQueries({ queryKey: ['stats'] })
      queryClient.invalidateQueries({ queryKey: ['card-quantities', card?.id] })
      queryClient.invalidateQueries({ queryKey: ['player-set-stats-detail'] })
      queryClient.invalidateQueries({ queryKey: ['player-set-stats'] })
    },
  })

  const debouncedSave = useCallback((qty: number) => {
    if (saveTimeout) {
      clearTimeout(saveTimeout)
    }
    
    const timeout = setTimeout(() => {
      updateMutation.mutate(qty)
    }, 500)
    
    setSaveTimeout(timeout)
  }, [saveTimeout, updateMutation])

  useEffect(() => {
    return () => {
      if (saveTimeout) {
        clearTimeout(saveTimeout)
      }
    }
  }, [saveTimeout])

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity < 0) return
    if (newQuantity > 99) return
    
    // Don't allow editing if viewing merged or read-only collection
    if (isReadOnlyCollection) {
      return
    }
    
    setQuantity(newQuantity)
    setInputValue(String(newQuantity))
    debouncedSave(newQuantity)
  }

  const handleInputChange = (value: string) => {
    setInputValue(value)
    
    // Don't allow editing if viewing merged or read-only collection
    if (isReadOnlyCollection) {
      return
    }
    
    const num = parseInt(value) || 0
    if (num >= 0 && num <= 99) {
      setQuantity(num)
      debouncedSave(num)
    }
  }

  const handleCreateCollection = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newCollectionName.trim()) return

    createCollectionMutation.mutate({
      name: newCollectionName.trim(),
      description: newCollectionDescription.trim() || undefined,
    })
  }

  if (!card) return null

  // Parse attributes from JSON string
  interface CardAttributes {
    counter?: string | number
    attribute?: string
    color?: string
    family?: string
    ability?: string
    trigger?: string
    slash?: string
    [key: string]: any
  }
  
  let attributes: CardAttributes = {}
  try {
    attributes = typeof card.attributes === 'string' 
      ? JSON.parse(card.attributes) 
      : card.attributes || {}
  } catch (e) {
    attributes = {}
  }

  const getColorClass = (color: string) => {
    const colorLower = color.toLowerCase()
    if (colorLower.includes('/')) {
      // Multi-color cards
      const colors = colorLower.split('/')
      if (colors.length === 2) {
        const colorMap: Record<string, string> = {
          'red': 'from-red-500',
          'blue': 'from-blue-500',
          'green': 'from-green-500',
          'purple': 'from-purple-500',
          'black': 'from-gray-900',
          'yellow': 'from-yellow-500'
        }
        const toColorMap: Record<string, string> = {
          'red': 'to-red-500',
          'blue': 'to-blue-500',
          'green': 'to-green-500',
          'purple': 'to-purple-500',
          'black': 'to-gray-900',
          'yellow': 'to-yellow-500'
        }
        const from = colorMap[colors[0].trim()] || 'from-gray-500'
        const to = toColorMap[colors[1].trim()] || 'to-gray-500'
        return `bg-gradient-to-r ${from} ${to}`
      }
    }
    // Single color cards
    switch (colorLower) {
      case 'red': return 'bg-red-500'
      case 'blue': return 'bg-blue-500'
      case 'green': return 'bg-green-500'
      case 'purple': return 'bg-purple-500'
      case 'black': return 'bg-gray-900'
      case 'yellow': return 'bg-yellow-500'
      default: return 'bg-gray-500'
    }
  }

  const getRarityIcon = (rarity: string) => {
    switch(rarity) {
      case 'L': return <Crown className="h-3 w-3" />
      case 'SR': return <Star className="h-3 w-3" />
      case 'R': return <Sparkles className="h-3 w-3" />
      default: return null
    }
  }

  const getTypeIcon = (type: string) => {
    const typeLower = type?.toLowerCase() || ''
    if (typeLower.includes('character')) return <Users className="h-3 w-3" />
    if (typeLower.includes('event')) return <Zap className="h-3 w-3" />
    if (typeLower.includes('stage')) return <Layers className="h-3 w-3" />
    if (typeLower.includes('leader')) return <Crown className="h-3 w-3" />
    return <Tag className="h-3 w-3" />
  }

  // Shared content rendered in both modal and inline modes
  const cardContent = (
    <>
      {/* Header Bar */}
      <div className={`sticky top-0 z-20 bg-white dark:bg-dark-surface border-b border-gray-200 dark:border-dark-border px-4 py-1.5 sm:py-2 ${inline ? 'rounded-t-lg' : ''}`}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm flex-shrink-0" title={card.language === 'ja' ? 'Japanese' : 'English'}>{card.language === 'ja' ? '🇯🇵' : '🇺🇸'}</span>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 className={`font-bold text-gray-900 dark:text-dark-text truncate ${inline ? 'text-lg sm:text-xl' : 'text-base sm:text-lg'}`}>{decodeHtmlEntities(card.name)}</h2>
                <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-dark-border rounded text-[10px] font-medium text-gray-600 dark:text-dark-muted flex-shrink-0">
                  {card.id}
                </span>
              </div>
              <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                {card.set_id?.toUpperCase()} — {card.set_name || 'Unknown Set'}
                {card.language === 'ja' && card.english_name && ` · ${card.english_name}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {(card as any).is_errata || (card as any).errata_of || card.has_errata ? (
              <button
                type="button"
                onClick={() => setErrataPairOpen((v) => !v)}
                className="px-2 py-1 hover:bg-amber-100 dark:hover:bg-amber-900/30 rounded-lg transition-colors text-xs font-medium text-amber-700 dark:text-amber-300 flex items-center gap-1"
                title={errataPairOpen ? 'Hide errata history' : 'Show errata history'}
                data-testid="card-errata-history-toggle"
              >
                {errataPairOpen ? 'Hide errata' : 'Errata history'}
              </button>
            ) : null}
            {isAdmin && (
              <button
                type="button"
                onClick={() => setAdminEditOpen(true)}
                className="p-1 hover:bg-amber-100 dark:hover:bg-amber-900/30 rounded-lg transition-colors"
                title="Admin edit card"
                data-testid="admin-edit-card-button"
              >
                <Pencil className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              </button>
            )}
            {!inline && (
              <a
                href={`/cards/${card.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1 hover:bg-gray-100 dark:hover:bg-dark-border rounded-lg transition-colors"
                title="Open in new tab"
              >
                <ExternalLink className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              </a>
            )}
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 dark:hover:bg-dark-border rounded-lg transition-colors"
              title={inline ? 'Go back' : 'Close'}
            >
              <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>
      </div>

      {errataPairOpen && card && (
        <div className="px-3 sm:px-4 pt-3" data-testid="card-detail-errata-pair-panel">
          <ErrataPairView cardId={card.id} />
        </div>
      )}

      <div className="flex flex-col sm:flex-row">
                  {/* Left Column - Image */}
                  <div className="sm:w-2/5 p-3 sm:p-4 bg-gray-50 dark:bg-dark-bg">
                    <div className="w-full max-w-xs mx-auto">
                      <CardImage
                        src={proxyImageUrl(card.image_url)}
                        alt={card.name}
                        cardId={card.id}
                        className="w-full rounded-lg shadow-lg"
                      />
                    </div>
                  </div>

                  {/* Right Column - Details and Controls */}
                  <div className="flex-1 p-3 sm:p-4">
                    {/* Collection Control */}
                    <div className={`rounded-xl mb-3 shadow-md border-2 transition-colors ${
                      currentCollection?.collection_type === 'stock'
                        ? 'bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-400 dark:border-amber-600'
                        : 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-400 dark:border-blue-600'
                    }`}>
                      {/* Collection Selector */}
                      <div className="flex items-center justify-between px-3 py-2.5 relative" ref={undefined}>
                        <button
                          onClick={() => setShowCollectionMenu(!showCollectionMenu)}
                          className="flex items-center gap-2 min-w-0 flex-1"
                        >
                          <span className={`text-[9px] uppercase tracking-widest font-bold whitespace-nowrap px-1.5 py-0.5 rounded-full ${
                            currentCollection?.collection_type === 'stock'
                              ? 'bg-amber-200 dark:bg-amber-800/50 text-amber-800 dark:text-amber-200'
                              : 'bg-blue-200 dark:bg-blue-800/50 text-blue-800 dark:text-blue-200'
                          }`}>
                            {currentCollection?.collection_type === 'stock' ? 'Selling' : 'Collecting'}
                          </span>
                          <span className={`text-sm font-bold truncate ${
                            currentCollection?.collection_type === 'stock'
                              ? 'text-amber-900 dark:text-amber-100'
                              : 'text-blue-900 dark:text-blue-100'
                          }`}>
                            {currentCollection?.is_default ? '⭐' : currentCollection?.collection_type === 'stock' ? '💰' : '📁'} {currentCollectionName || 'Select collection'}
                          </span>
                          <ChevronDown className={`h-3.5 w-3.5 flex-shrink-0 transition-transform ${showCollectionMenu ? 'rotate-180' : ''} ${
                            currentCollection?.collection_type === 'stock' ? 'text-amber-600 dark:text-amber-400' : 'text-blue-600 dark:text-blue-400'
                          }`} />
                        </button>
                        {isReadOnlyCollection && (
                          <Lock className="h-3 w-3 text-orange-400 flex-shrink-0 ml-1" />
                        )}

                        {/* Collection Dropdown Menu */}
                        {showCollectionMenu && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl shadow-2xl z-50 max-h-60 overflow-y-auto">
                            {/* Personal collections */}
                            {userCollections.filter((c: any) => c.collection_type !== 'stock' && !c.is_hidden).length > 0 && (
                              <>
                                <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-blue-500 dark:text-blue-400 font-bold bg-blue-50/50 dark:bg-blue-900/10">
                                  Personal Collections
                                </div>
                                {userCollections.filter((c: any) => c.collection_type !== 'stock' && !c.is_hidden).map((collection: any) => (
                                  <button
                                    key={collection.id}
                                    onClick={() => {
                                      setCurrentCollectionId(collection.id)
                                      setShowCollectionMenu(false)
                                      const collectionData = collectionQuantities.find((cq: any) => cq.collectionId === collection.id)
                                      const newQuantity = collectionData?.quantity || 0
                                      setQuantity(newQuantity)
                                      setInputValue(String(newQuantity))
                                    }}
                                    className={`w-full text-left px-3 py-2 text-xs hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors flex items-center justify-between ${
                                      currentCollectionId === collection.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                                    }`}
                                  >
                                    <span className={`flex items-center gap-2 ${currentCollectionId === collection.id ? 'font-bold' : ''}`}>
                                      <span>{collection.is_default ? '⭐' : '📁'}</span>
                                      <span>{collection.name}</span>
                                      {!!collection.is_locked && <Lock className="h-3 w-3 text-orange-400" />}
                                    </span>
                                    {currentCollectionId === collection.id && <Check className="h-3.5 w-3.5 text-blue-500" />}
                                  </button>
                                ))}
                              </>
                            )}
                            {/* Stock collections */}
                            {userCollections.filter((c: any) => c.collection_type === 'stock' && !c.is_hidden).length > 0 && (
                              <>
                                <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-amber-600 dark:text-amber-400 font-bold border-t border-gray-100 dark:border-dark-border bg-amber-50/50 dark:bg-amber-900/10">
                                  Stock / For Sale
                                </div>
                                {userCollections.filter((c: any) => c.collection_type === 'stock' && !c.is_hidden).map((collection: any) => (
                                  <button
                                    key={collection.id}
                                    onClick={() => {
                                      setCurrentCollectionId(collection.id)
                                      setShowCollectionMenu(false)
                                      const collectionData = collectionQuantities.find((cq: any) => cq.collectionId === collection.id)
                                      const newQuantity = collectionData?.quantity || 0
                                      setQuantity(newQuantity)
                                      setInputValue(String(newQuantity))
                                    }}
                                    className={`w-full text-left px-3 py-2 text-xs hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors flex items-center justify-between ${
                                      currentCollectionId === collection.id ? 'bg-amber-50 dark:bg-amber-900/20' : ''
                                    }`}
                                  >
                                    <span className={`flex items-center gap-2 ${currentCollectionId === collection.id ? 'font-bold' : ''}`}>
                                      <span>💰</span>
                                      <span>{collection.name}</span>
                                      {!!collection.is_locked && <Lock className="h-3 w-3 text-orange-400" />}
                                    </span>
                                    {currentCollectionId === collection.id && <Check className="h-3.5 w-3.5 text-amber-500" />}
                                  </button>
                                ))}
                              </>
                            )}
                            {/* New collection button */}
                            <div className="border-t border-gray-200 dark:border-dark-border">
                              <button
                                onClick={() => {
                                  setShowCollectionMenu(false)
                                  setShowCreateModal(true)
                                }}
                                className="w-full text-left px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-dark-border transition-colors flex items-center gap-2 text-blue-600 dark:text-blue-400"
                              >
                                <FolderPlus className="h-3 w-3" />
                                New Collection
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between px-3 pb-2.5">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <div className="flex items-center bg-white dark:bg-dark-surface rounded-md border border-gray-300 dark:border-dark-border">
                            <button
                              onClick={() => handleQuantityChange(quantity - 1)}
                              className="p-1.5 hover:bg-gray-100 dark:hover:bg-dark-border rounded-l-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={quantity === 0 || isReadOnlyCollection}
                              title={isReadOnlyCollection ? 'This collection is read-only' : 'Decrease quantity'}
                            >
                              <Minus className="h-3.5 w-3.5" />
                            </button>
                            <input
                              type="text"
                              value={inputValue}
                              onChange={(e) => handleInputChange(e.target.value)}
                              onBlur={() => setInputValue(String(quantity))}
                              className="w-7 sm:w-10 text-center font-semibold bg-transparent focus:outline-none text-xs sm:text-sm dark:text-dark-text disabled:opacity-50 disabled:cursor-not-allowed"
                              min="0"
                              max="99"
                              disabled={isReadOnlyCollection}
                              title={isReadOnlyCollection ? 'This collection is read-only' : 'Enter quantity'}
                            />
                            <button
                              onClick={() => handleQuantityChange(quantity + 1)}
                              className="p-1.5 hover:bg-gray-100 dark:hover:bg-dark-border rounded-r-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={quantity >= 99 || isReadOnlyCollection}
                              title={isReadOnlyCollection ? 'This collection is read-only' : 'Increase quantity'}
                            >
                              <Plus className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          {/* Quick set buttons */}
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-gray-600 dark:text-dark-muted hidden sm:inline">Set to:</span>
                            <button
                              onClick={() => handleQuantityChange(1)}
                              className={`px-3 py-1.5 sm:px-3 sm:py-1.5 text-sm font-medium rounded border transition-colors ${
                                quantity === 1 
                                  ? 'bg-blue-500 text-white border-blue-600' 
                                  : 'bg-gray-100 dark:bg-dark-border hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-dark-text border-gray-300 dark:border-gray-600'
                              }`}
                            >
                              1
                            </button>
                            {!isLeader && (
                              <button
                                onClick={() => handleQuantityChange(4)}
                                className={`px-3 py-1.5 sm:px-3 sm:py-1.5 text-sm font-medium rounded border transition-colors ${
                                  quantity === 4 
                                    ? 'bg-blue-500 text-white border-blue-600' 
                                    : 'bg-gray-100 dark:bg-dark-border hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-dark-text border-gray-300 dark:border-gray-600'
                                }`}
                              >
                                4
                              </button>
                            )}
                          </div>
                        </div>
                        {quantity > 0 && (
                          <span className="px-2 py-1 bg-green-500 text-white rounded-full text-xs font-medium">
                            Owned
                          </span>
                        )}
                      </div>
                      {updateMutation.isPending && (
                        <div className="mt-2 text-xs text-gray-500 dark:text-dark-muted">Saving...</div>
                      )}
                    </div>

                    {/* Store Listings Section */}
                    <div className="mt-2 bg-indigo-50/50 dark:bg-indigo-900/10 p-2.5 rounded-lg border border-indigo-200 dark:border-indigo-900/50">
                      <button
                        onClick={() => setStoreExpanded(!storeExpanded)}
                        className="flex items-center justify-between w-full text-left group"
                      >
                        <div className="flex items-center gap-1.5">
                          <Store className="w-3.5 h-3.5 text-indigo-500" />
                          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Store</span>
                          {cardStoreListings.length > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-full font-medium">
                              {cardStoreListings.length}
                            </span>
                          )}
                        </div>
                        {storeExpanded ? <ChevronUp className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />}
                      </button>

                      {storeExpanded && (
                        <div className="mt-2 space-y-2">
                          {/* Existing listings */}
                          {cardStoreListings.length > 0 && (
                            <div className="space-y-1">
                              {cardStoreListings.map((sl: any) => (
                                <div key={sl.id} className="flex items-center justify-between px-2 py-1.5 bg-indigo-50 dark:bg-indigo-900/20 rounded-md">
                                  <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-semibold text-indigo-700 dark:text-indigo-400">{sl.condition}</span>
                                    <span className="text-xs font-medium text-gray-900 dark:text-white">
                                      {sl.is_nfs ? 'NFS' : `€${sl.price.toFixed(2)}`}
                                    </span>
                                    <span className="text-[10px] text-gray-500 dark:text-dark-muted">&times;{sl.quantity}</span>
                                  </div>
                                  <button
                                    onClick={() => deleteStoreMutation.mutate(sl.id)}
                                    className="p-0.5 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
                                    title="Remove from store"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Add new listing form */}
                          <div className="flex flex-wrap items-center gap-1.5 p-2 bg-gray-50 dark:bg-dark-bg rounded-md">
                            <select
                              value={newStoreCondition}
                              onChange={(e) => setNewStoreCondition(e.target.value)}
                              className="px-1.5 py-1 border border-gray-300 dark:border-gray-600 rounded text-[10px] bg-white dark:bg-gray-700"
                            >
                              <option value="M">Mint</option>
                              <option value="NM">Near Mint</option>
                              <option value="EX">Excellent</option>
                              <option value="GD">Good/Played</option>
                            </select>
                            {!newStoreNfs && (
                              <input
                                type="number"
                                step="0.01"
                                placeholder="Price"
                                value={newStorePrice}
                                onChange={(e) => setNewStorePrice(e.target.value)}
                                className="w-16 px-1.5 py-1 border border-gray-300 dark:border-gray-600 rounded text-[10px] text-center bg-white dark:bg-gray-700"
                              />
                            )}
                            <input
                              type="number"
                              min="1"
                              placeholder="Qty"
                              value={newStoreQty}
                              onChange={(e) => setNewStoreQty(e.target.value)}
                              className="w-10 px-1 py-1 border border-gray-300 dark:border-gray-600 rounded text-[10px] text-center bg-white dark:bg-gray-700"
                            />
                            <label className="flex items-center gap-0.5 text-[10px] cursor-pointer">
                              <input
                                type="checkbox"
                                checked={newStoreNfs}
                                onChange={(e) => setNewStoreNfs(e.target.checked)}
                                className="rounded w-3 h-3"
                              />
                              NFS
                            </label>
                            <button
                              onClick={() => card && createStoreMutation.mutate({
                                card_id: card.id,
                                condition: newStoreCondition,
                                price: newStoreNfs ? 0 : parseFloat(newStorePrice),
                                quantity: parseInt(newStoreQty) || 1,
                                is_nfs: newStoreNfs,
                              })}
                              disabled={(!newStoreNfs && !newStorePrice) || createStoreMutation.isPending}
                              className="px-2 py-1 bg-indigo-600 text-white rounded text-[10px] font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-0.5"
                            >
                              <Plus className="w-2.5 h-2.5" />
                              Add
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Collection Breakdown */}
                    {collectionQuantities.length > 0 && (
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-2.5 rounded-lg mb-3 border border-blue-200 dark:border-blue-900/50">
                        <h4 className="text-xs font-semibold text-gray-900 dark:text-dark-text mb-1.5 flex items-center gap-1.5">
                          <Layers className="h-3.5 w-3.5" />
                          Collection Breakdown
                        </h4>
                        <div className="space-y-1">
                          {collectionQuantities.filter((cq: any) => cq.quantity > 0 || cq.foilQuantity > 0).map((cq: any) => {
                            const cqTotal = cq.quantity + cq.foilQuantity
                            return (
                              <div key={cq.collectionId} className="flex items-center justify-between text-xs">
                                <span className="text-gray-600 dark:text-dark-muted">{cq.collectionName}:</span>
                                <div className="flex items-center gap-2">
                                  {cq.quantity > 0 && (
                                    <span className="font-medium text-gray-900 dark:text-dark-text">
                                      {cq.quantity} regular
                                    </span>
                                  )}
                                  {cq.foilQuantity > 0 && (
                                    <span className="font-medium text-yellow-600 dark:text-yellow-400">
                                      {cq.foilQuantity} foil
                                    </span>
                                  )}
                                  {cqTotal > 0 && (cardPrice !== undefined || ctListingPrice !== undefined) && (
                                    <span className="flex items-center gap-1">
                                      {cardPrice !== undefined && (
                                        <span className="font-medium text-green-600 dark:text-green-400" title="Market low">
                                          {formatCurrency(cardPrice * cqTotal)}
                                        </span>
                                      )}
                                      {cardPrice !== undefined && ctListingPrice !== undefined && (
                                        <span className="text-gray-300 dark:text-gray-600">/</span>
                                      )}
                                      {ctListingPrice !== undefined && (
                                        <span className="font-medium text-amber-600 dark:text-amber-400" title="CT listing">
                                          {formatCurrency(ctListingPrice * cqTotal)}
                                        </span>
                                      )}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )
                          })}
                          {collectionQuantities.filter((cq: any) => cq.quantity > 0 || cq.foilQuantity > 0).length > 1 && (
                            <div className="border-t border-blue-200 dark:border-blue-800 pt-1 mt-1">
                              <div className="flex items-center justify-between text-xs font-semibold">
                                <span className="text-gray-900 dark:text-dark-text">Total Owned:</span>
                                <div className="flex items-center gap-2">
                                  <span className="text-blue-600 dark:text-blue-400">{totalOwned} {isLeader ? '(max 1 needed)' : ''}</span>
                                  {totalOwned > 0 && (cardPrice !== undefined || ctListingPrice !== undefined) && (
                                    <span className="flex items-center gap-1">
                                      {cardPrice !== undefined && (
                                        <span className="text-green-600 dark:text-green-400" title="Market low">
                                          {formatCurrency(cardPrice * totalOwned)}
                                        </span>
                                      )}
                                      {cardPrice !== undefined && ctListingPrice !== undefined && (
                                        <span className="text-gray-300 dark:text-gray-600">/</span>
                                      )}
                                      {ctListingPrice !== undefined && (
                                        <span className="text-amber-600 dark:text-amber-400" title="CT listing">
                                          {formatCurrency(ctListingPrice * totalOwned)}
                                        </span>
                                      )}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        {totalOwned === 0 && (
                          <p className="text-xs text-gray-500 dark:text-dark-muted italic">Not in any collection yet</p>
                        )}
                      </div>
                    )}

                    {/* Compact Attributes Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mb-3">
                      {/* Rarity */}
                      <div className="flex items-center gap-1.5 px-2 py-1.5 bg-gray-50 dark:bg-dark-border rounded-lg">
                        <div className={`p-1.5 rounded-full text-white ${
                          card.rarity === 'L' ? 'bg-yellow-600' :
                          card.rarity === 'SR' ? 'bg-purple-600' :
                          card.rarity === 'R' ? 'bg-red-600' :
                          card.rarity === 'UC' ? 'bg-blue-600' :
                          'bg-gray-600'
                        }`}>
                          {getRarityIcon(card.rarity || '') || <Star className="h-3 w-3" />}
                        </div>
                        <div>
                          <p className="text-[9px] text-gray-500 dark:text-dark-muted uppercase">Rarity</p>
                          <p className="text-sm font-semibold dark:text-dark-text">{card.rarity || 'C'}</p>
                        </div>
                      </div>

                      {/* Type */}
                      <div className="flex items-center gap-1.5 px-2 py-1.5 bg-gray-50 dark:bg-dark-border rounded-lg">
                        <div className="p-1.5 bg-purple-100 dark:bg-purple-900/30 rounded-full text-purple-600 dark:text-purple-400">
                          {getTypeIcon(card.type || '')}
                        </div>
                        <div>
                          <p className="text-[9px] text-gray-500 dark:text-dark-muted uppercase">Type</p>
                          <p className="text-sm font-semibold dark:text-dark-text">
                            {card.type || '-'}
                            {isLeader && <span className="text-xs text-gray-500 dark:text-dark-muted ml-1">(1 copy max)</span>}
                          </p>
                        </div>
                      </div>

                      {/* Color */}
                      <div className="flex items-center gap-1.5 px-2 py-1.5 bg-gray-50 dark:bg-dark-border rounded-lg">
                        <div className="p-1.5 rounded-full text-white flex items-center justify-center" 
                             style={{ 
                               background: attributes.color?.toLowerCase().includes('/') 
                                 ? `linear-gradient(135deg, ${
                                     attributes.color.toLowerCase().includes('red') ? '#ef4444' :
                                     attributes.color.toLowerCase().includes('blue') ? '#3b82f6' :
                                     attributes.color.toLowerCase().includes('green') ? '#10b981' :
                                     attributes.color.toLowerCase().includes('purple') ? '#a855f7' :
                                     attributes.color.toLowerCase().includes('yellow') ? '#eab308' :
                                     attributes.color.toLowerCase().includes('black') ? '#111827' : '#6b7280'
                                   } 0%, ${
                                     attributes.color.toLowerCase().split('/')[1]?.includes('red') ? '#ef4444' :
                                     attributes.color.toLowerCase().split('/')[1]?.includes('blue') ? '#3b82f6' :
                                     attributes.color.toLowerCase().split('/')[1]?.includes('green') ? '#10b981' :
                                     attributes.color.toLowerCase().split('/')[1]?.includes('purple') ? '#a855f7' :
                                     attributes.color.toLowerCase().split('/')[1]?.includes('yellow') ? '#eab308' :
                                     attributes.color.toLowerCase().split('/')[1]?.includes('black') ? '#111827' : '#6b7280'
                                   } 100%)`
                                 : attributes.color?.toLowerCase() === 'red' ? '#ef4444' :
                                   attributes.color?.toLowerCase() === 'blue' ? '#3b82f6' :
                                   attributes.color?.toLowerCase() === 'green' ? '#10b981' :
                                   attributes.color?.toLowerCase() === 'purple' ? '#a855f7' :
                                   attributes.color?.toLowerCase() === 'yellow' ? '#eab308' :
                                   attributes.color?.toLowerCase() === 'black' ? '#111827' : '#6b7280'
                             }}>
                          <Palette className="h-3 w-3" />
                        </div>
                        <div>
                          <p className="text-[9px] text-gray-500 dark:text-dark-muted uppercase">Color</p>
                          <p className="text-sm font-semibold dark:text-dark-text">{attributes.color || '-'}</p>
                        </div>
                      </div>

                      {/* Family */}
                      <div className="flex items-center gap-1.5 px-2 py-1.5 bg-gray-50 dark:bg-dark-border rounded-lg">
                        <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-full text-blue-600 dark:text-blue-400">
                          <Users className="h-3 w-3" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-[9px] text-gray-500 dark:text-dark-muted uppercase">Family</p>
                          <p className="text-sm font-semibold dark:text-dark-text truncate">{attributes.family || '-'}</p>
                        </div>
                      </div>

                      {/* Power */}
                      <div className="flex items-center gap-1.5 px-2 py-1.5 bg-gray-50 dark:bg-dark-border rounded-lg">
                        <div className="p-1.5 bg-red-100 dark:bg-red-900/30 rounded-full text-red-600 dark:text-red-400">
                          <Swords className="h-3 w-3" />
                        </div>
                        <div>
                          <p className="text-[9px] text-gray-500 dark:text-dark-muted uppercase">Power</p>
                          <p className="text-sm font-semibold dark:text-dark-text">{card.power !== null && card.power !== undefined ? card.power : '-'}</p>
                        </div>
                      </div>

                      {/* Attribute (or Slash if present) */}
                      <div className="flex items-center gap-1.5 px-2 py-1.5 bg-gray-50 dark:bg-dark-border rounded-lg">
                        <div className="p-1.5 bg-orange-100 dark:bg-orange-900/30 rounded-full text-orange-600 dark:text-orange-400">
                          <Tag className="h-3 w-3" />
                        </div>
                        <div>
                          <p className="text-[9px] text-gray-500 dark:text-dark-muted uppercase">Attribute</p>
                          <p className="text-sm font-semibold dark:text-dark-text">
                            {attributes.slash || attributes.attribute || '-'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Cost, Counter, Life - Inline with abilities if present */}
                    {(card.cost !== null || attributes.counter || card.life) && (
                      <div className="flex items-center gap-4 mb-2 text-sm">
                        {card.cost !== null && card.cost !== undefined && (
                          <div className="flex items-center gap-1">
                            <Sparkles className="h-3 w-3 text-yellow-600 dark:text-yellow-400" />
                            <span className="text-xs text-gray-600 dark:text-dark-muted">Cost:</span>
                            <span className="font-bold dark:text-dark-text">{card.cost}</span>
                          </div>
                        )}
                        {attributes.counter && attributes.counter !== '-' && (
                          <div className="flex items-center gap-1">
                            <Shield className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                            <span className="text-xs text-gray-600 dark:text-dark-muted">Counter:</span>
                            <span className="font-bold dark:text-dark-text">+{attributes.counter}</span>
                          </div>
                        )}
                        {card.life && (
                          <div className="flex items-center gap-1">
                            <Heart className="h-3 w-3 text-green-600 dark:text-green-400" />
                            <span className="text-xs text-gray-600 dark:text-dark-muted">Life:</span>
                            <span className="font-bold dark:text-dark-text">{card.life}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Text Abilities - Ultra Compact */}
                    <div className="space-y-1.5">
                      {attributes.ability && (
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded-lg border border-yellow-200 dark:border-yellow-900/50">
                          <div className="flex items-center gap-1 mb-0.5">
                            <Zap className="h-3 w-3 text-yellow-600 dark:text-yellow-400" />
                            <p className="text-[10px] font-semibold text-yellow-700 dark:text-yellow-400 uppercase tracking-wide">Ability</p>
                          </div>
                          <p className="text-xs dark:text-dark-text leading-relaxed">{decodeHtmlEntities(attributes.ability)}</p>
                        </div>
                      )}

                      {attributes.trigger && attributes.trigger !== '' && (
                        <div className="bg-red-50 dark:bg-red-900/20 p-2 rounded-lg border border-red-200 dark:border-red-900/50">
                          <div className="flex items-center gap-1 mb-0.5">
                            <AlertTriangle className="h-3 w-3 text-red-600 dark:text-red-400" />
                            <p className="text-[10px] font-semibold text-red-700 dark:text-red-400 uppercase tracking-wide">Trigger</p>
                          </div>
                          <p className="text-xs dark:text-dark-text leading-relaxed">{decodeHtmlEntities(attributes.trigger)}</p>
                        </div>
                      )}
                    </div>

                    {/* Market Price Section - Collapsible */}
                    <div className="mt-3 border-t dark:border-dark-border pt-2">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setPriceExpanded(!priceExpanded)}
                          className="flex-1 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-dark-border -mx-2 px-2 py-1 rounded-lg transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <TrendingUp className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                            <span className="text-sm font-semibold text-gray-900 dark:text-dark-text">
                              Market Price
                            </span>
                            <span className="text-xs">{isCurrentJP ? '\u{1F1EF}\u{1F1F5}' : '\u{1F1FA}\u{1F1F8}'}</span>
                          </div>
                          {priceExpanded ? (
                            <ChevronUp className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                          )}
                        </button>
                        {priceExpanded && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              if (card) syncPriceMutation.mutate(card.id)
                            }}
                            disabled={syncPriceMutation.isPending}
                            title="Refresh price from CardTrader"
                            className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-dark-border rounded-md transition-colors disabled:opacity-50"
                          >
                            {syncPriceMutation.isPending ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <RefreshCw className="h-3.5 w-3.5" />
                            )}
                          </button>
                        )}
                      </div>

                      {priceExpanded && (
                        <div className="mt-3">
                          {priceLoading ? (
                            <div className="flex justify-center py-3">
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                            </div>
                          ) : (priceData?.price || priceData?.price?.cardmarket) ? (
                            <div className="space-y-3">
                              {/* 2-row price grid: CM row + CT row */}
                              <div className="space-y-1.5">
                                {/* Cardmarket row */}
                                {priceData.price.cardmarket && (
                                  <div>
                                    <div className="flex items-center gap-1 mb-1">
                                      <span className="text-[9px] font-bold text-orange-600 dark:text-orange-400 uppercase tracking-wider">Cardmarket</span>
                                      <span className="text-[9px] text-gray-400 dark:text-gray-500">({priceData.price.cardmarket.quantity_available || 0} listings)</span>
                                    </div>
                                    <div className="grid grid-cols-4 gap-1">
                                      <div className="bg-orange-50 dark:bg-orange-900/15 rounded-md p-1.5 text-center">
                                        <p className="text-[9px] text-gray-500 dark:text-gray-400 uppercase">From</p>
                                        <p className="text-xs font-bold text-orange-700 dark:text-orange-400">{formatCurrency(priceData.price.cardmarket.min_price_cents / 100)}</p>
                                      </div>
                                      <div className="bg-orange-50 dark:bg-orange-900/15 rounded-md p-1.5 text-center">
                                        <p className="text-[9px] text-gray-500 dark:text-gray-400 uppercase">Trend</p>
                                        <p className="text-xs font-bold text-orange-700 dark:text-orange-400">{formatCurrency((priceData.price.cardmarket.median_price_cents ?? 0) / 100)}</p>
                                      </div>
                                      <div className="bg-orange-50 dark:bg-orange-900/15 rounded-md p-1.5 text-center">
                                        <p className="text-[9px] text-gray-500 dark:text-gray-400 uppercase">30d Avg</p>
                                        <p className="text-xs font-bold text-orange-700 dark:text-orange-400">{formatCurrency(priceData.price.cardmarket.avg_price_cents / 100)}</p>
                                      </div>
                                      <div className="bg-orange-50 dark:bg-orange-900/15 rounded-md p-1.5 text-center">
                                        <p className="text-[9px] text-gray-500 dark:text-gray-400 uppercase">Max</p>
                                        <p className="text-xs font-bold text-orange-700 dark:text-orange-400">{formatCurrency(priceData.price.cardmarket.max_price_cents / 100)}</p>
                                      </div>
                                    </div>
                                    {/* CM Listings toggle */}
                                    <button
                                      onClick={() => setListingsExpanded(!listingsExpanded)}
                                      className="mt-1 text-[9px] text-orange-500 dark:text-orange-400 hover:underline"
                                    >
                                      {listingsExpanded ? 'Hide listings' : `Show ${priceData.price.cardmarket.quantity_available || ''} listings`}
                                    </button>
                                    {listingsExpanded && (
                                      <div className="mt-1 max-h-36 overflow-y-auto rounded border border-orange-200 dark:border-orange-800/30">
                                        <table className="w-full text-[10px]">
                                          <thead className="sticky top-0 bg-orange-50 dark:bg-orange-900/30">
                                            <tr className="text-gray-500 dark:text-gray-400">
                                              <th className="text-left px-1.5 py-0.5 font-medium">Seller</th>
                                              <th className="text-center px-1 py-0.5 font-medium">Cond</th>
                                              <th className="text-center px-1 py-0.5 font-medium">Qty</th>
                                              <th className="text-right px-1.5 py-0.5 font-medium">Price</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {cmListingsData?.listings
                                              ?.slice()
                                              .sort((a, b) => a.p - b.p)
                                              .map((l, i) => (
                                                <tr key={i} className={i % 2 === 0 ? 'bg-white dark:bg-dark-card' : 'bg-orange-50/50 dark:bg-orange-900/10'}>
                                                  <td className="px-1.5 py-0.5 truncate max-w-[100px] text-gray-700 dark:text-gray-300" title={l.d || undefined}>{l.s}{l.d ? ' *' : ''}</td>
                                                  <td className="px-1 py-0.5 text-center text-gray-500 dark:text-gray-400">{l.c || '-'}</td>
                                                  <td className="px-1 py-0.5 text-center text-gray-500 dark:text-gray-400">{l.q}</td>
                                                  <td className="px-1.5 py-0.5 text-right font-semibold text-orange-700 dark:text-orange-400">{formatCurrency(l.p)}</td>
                                                </tr>
                                              )) ?? (
                                              <tr><td colSpan={4} className="text-center py-2 text-gray-400 dark:text-gray-500">Loading...</td></tr>
                                            )}
                                            {cmListingsData?.listings?.length === 0 && (
                                              <tr><td colSpan={4} className="text-center py-2 text-gray-400 dark:text-gray-500">No listings scraped yet</td></tr>
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </div>
                                )}
                                {/* CardTrader row */}
                                {priceData.price.min_price_cents > 0 && priceData.price.source !== 'cardmarket' && (
                                  <div>
                                    <div className="flex items-center gap-1 mb-1">
                                      <span className="text-[9px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider">CardTrader</span>
                                      <span className="text-[9px] text-gray-400 dark:text-gray-500">({priceData.price.quantity_available || 0} listings)</span>
                                    </div>
                                    <div className="grid grid-cols-4 gap-1">
                                      <div className="bg-blue-50 dark:bg-blue-900/15 rounded-md p-1.5 text-center">
                                        <p className="text-[9px] text-gray-500 dark:text-gray-400 uppercase">Min</p>
                                        <p className="text-xs font-bold text-blue-700 dark:text-blue-400">{formatCurrency(priceData.price.min_price_cents / 100)}</p>
                                      </div>
                                      <div className="bg-blue-50 dark:bg-blue-900/15 rounded-md p-1.5 text-center">
                                        <p className="text-[9px] text-gray-500 dark:text-gray-400 uppercase">Avg</p>
                                        <p className="text-xs font-bold text-blue-700 dark:text-blue-400">{formatCurrency(priceData.price.avg_price_cents / 100)}</p>
                                      </div>
                                      <div className="bg-blue-50 dark:bg-blue-900/15 rounded-md p-1.5 text-center">
                                        <p className="text-[9px] text-gray-500 dark:text-gray-400 uppercase">Median</p>
                                        <p className="text-xs font-bold text-blue-700 dark:text-blue-400">{formatCurrency((priceData.price.median_price_cents ?? 0) / 100)}</p>
                                      </div>
                                      <div className="bg-blue-50 dark:bg-blue-900/15 rounded-md p-1.5 text-center">
                                        <p className="text-[9px] text-gray-500 dark:text-gray-400 uppercase">Max</p>
                                        <p className="text-xs font-bold text-blue-700 dark:text-blue-400">{formatCurrency(priceData.price.max_price_cents / 100)}</p>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                              {/* Your CT listing */}
                              {ctListingPrice !== undefined && (
                                <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-2 flex items-center justify-between">
                                  <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase">Your CT Listing</span>
                                  <span className="text-sm font-bold text-amber-700 dark:text-amber-400">{formatCurrency(ctListingPrice)}</span>
                                </div>
                              )}
                              {/* Cross-language price */}
                              {counterpartPriceData?.price && counterpartPriceData.price.min_price_cents > 0 && (
                                <div className="bg-gray-50 dark:bg-gray-800/30 rounded-lg p-2 flex items-center justify-between">
                                  <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase flex items-center gap-1">
                                    <span className="text-xs">{counterpartFlag}</span> {counterpartLabel} Price
                                  </span>
                                  <div className="text-right">
                                    <span className="text-sm font-bold text-gray-700 dark:text-gray-300">{formatCurrency(counterpartPriceData.price.min_price_cents / 100)}</span>
                                    <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-1">min</span>
                                  </div>
                                </div>
                              )}
                              {/* Price trend from history */}
                              {historyData?.history && historyData.history.length >= 2 && (() => {
                                const oldest = historyData.history[0]
                                const newest = historyData.history[historyData.history.length - 1]
                                const oldAvg = oldest.avg_price_cents
                                const newAvg = newest.avg_price_cents
                                const diff = newAvg - oldAvg
                                const pctChange = oldAvg > 0 ? ((diff / oldAvg) * 100) : 0
                                const isUp = diff > 0
                                const isFlat = Math.abs(pctChange) < 1
                                return (
                                  <div className={`flex items-center gap-1.5 text-xs px-2 py-1.5 rounded-lg ${
                                    isFlat
                                      ? 'bg-gray-50 dark:bg-gray-800/30 text-gray-600 dark:text-gray-400'
                                      : isUp
                                        ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                                        : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                                  }`}>
                                    {isFlat ? (
                                      <TrendingUp className="h-3 w-3" />
                                    ) : isUp ? (
                                      <TrendingUp className="h-3 w-3" />
                                    ) : (
                                      <TrendingDown className="h-3 w-3" />
                                    )}
                                    <span>
                                      {isFlat ? 'Stable' : `${isUp ? '+' : ''}${pctChange.toFixed(1)}%`}
                                      {' '}over {historyData.days}d
                                    </span>
                                  </div>
                                )
                              })()}
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[9px] text-gray-400 dark:text-dark-muted mt-1">
                                {priceData.price.cardmarket?.last_updated && (
                                  <span title={new Date(priceData.price.cardmarket.last_updated).toLocaleString()}>
                                    CM scan: {formatScanAge(priceData.price.cardmarket.last_updated)}
                                  </span>
                                )}
                                {priceData.price.last_updated && priceData.price.source !== 'cardmarket' && (
                                  <span title={new Date(priceData.price.last_updated).toLocaleString()}>
                                    CT scan: {formatScanAge(priceData.price.last_updated)}
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500 dark:text-dark-muted text-center py-2">No price data available</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* External Links & Feedback */}
                    <div className="mt-3 border-t dark:border-dark-border pt-2">
                      {/* CM URL inline edit */}
                      {editingCmUrl && (
                        <div className="flex items-center gap-1.5 mb-2">
                          <input
                            type="url"
                            value={cmUrlInput}
                            onChange={(e) => setCmUrlInput(e.target.value)}
                            placeholder="Paste Cardmarket URL..."
                            className="flex-1 text-xs px-2 py-1.5 border border-orange-300 dark:border-orange-700 rounded-md bg-white dark:bg-dark-card text-gray-800 dark:text-dark-text focus:outline-none focus:ring-1 focus:ring-orange-500"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                const clean = cmUrlInput.split('?')[0].split('#')[0].trim()
                                if (clean) {
                                  cardsApi.updateCardmarketUrl(card.id, clean).then(() => {
                                    toast.success('Cardmarket URL updated')
                                    setEditingCmUrl(false)
                                    // Refresh variants to pick up new URL
                                    fetchCardVariants()
                                  }).catch(() => toast.error('Failed to update URL'))
                                }
                              } else if (e.key === 'Escape') {
                                setEditingCmUrl(false)
                              }
                            }}
                          />
                          <button
                            onClick={() => {
                              const clean = cmUrlInput.split('?')[0].split('#')[0].trim()
                              if (clean) {
                                cardsApi.updateCardmarketUrl(card.id, clean).then(() => {
                                  toast.success('Cardmarket URL updated')
                                  setEditingCmUrl(false)
                                  fetchCardVariants()
                                }).catch(() => toast.error('Failed to update URL'))
                              }
                            }}
                            className="p-1.5 text-orange-600 hover:bg-orange-100 dark:hover:bg-orange-900/30 rounded-md"
                          >
                            <Check className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => setEditingCmUrl(false)}
                            className="p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-border rounded-md"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        {/* Cardmarket link - use variant data if card prop lacks it */}
                        {(card.cardmarket_url || variants.find(v => v.id === card.id)?.cardmarket_url) ? (
                          <a
                            href={card.cardmarket_url || variants.find(v => v.id === card.id)?.cardmarket_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/30 rounded-lg py-2 transition-colors"
                          >
                            <ExternalLink className="h-3 w-3" />
                            Cardmarket
                          </a>
                        ) : (
                          <div className="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-dark-bg rounded-lg py-2">
                            <ExternalLink className="h-3 w-3" />
                            No CM link
                          </div>
                        )}
                        {/* Fix CM URL button */}
                        <button
                          onClick={() => {
                            setCmUrlInput((card.cardmarket_url || variants.find(v => v.id === card.id)?.cardmarket_url) || '')
                            setEditingCmUrl(!editingCmUrl)
                          }}
                          className="flex items-center justify-center gap-1 text-xs font-medium text-orange-500 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/30 rounded-lg py-2 px-2.5 transition-colors"
                          title="Fix Cardmarket URL"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                        {/* CardTrader link */}
                        {priceData?.price?.blueprint_id > 0 && (
                          <a
                            href={`https://www.cardtrader.com/cards/${priceData?.price?.blueprint_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg py-2 transition-colors"
                          >
                            <ExternalLink className="h-3 w-3" />
                            CardTrader
                          </a>
                        )}
                        {/* Report Issue button */}
                        <button
                          onClick={() => setShowFeedback(!showFeedback)}
                          className={`flex items-center justify-center gap-1.5 text-xs font-medium rounded-lg py-2 px-3 transition-colors ${
                            showFeedback
                              ? 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30'
                              : 'text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 bg-gray-50 dark:bg-dark-bg hover:bg-red-50 dark:hover:bg-red-900/20'
                          }`}
                        >
                          <MessageSquareWarning className="h-3 w-3" />
                          Report
                        </button>
                      </div>

                      {/* Feedback form */}
                      {showFeedback && (
                        <div className="mt-2 p-3 bg-gray-50 dark:bg-dark-bg rounded-lg space-y-2">
                          <div className="flex gap-1.5 flex-wrap">
                            {[
                              { value: 'link', label: 'Link' },
                              { value: 'pricing', label: 'Pricing' },
                              { value: 'data', label: 'Data' },
                              { value: 'image', label: 'Image' },
                              { value: 'other', label: 'Other' },
                            ].map(cat => (
                              <button
                                key={cat.value}
                                onClick={() => setFeedbackCategory(cat.value)}
                                className={`text-[10px] px-2 py-0.5 rounded-full transition-colors ${
                                  feedbackCategory === cat.value
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-200 dark:bg-dark-border text-gray-600 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600'
                                }`}
                              >
                                {cat.label}
                              </button>
                            ))}
                          </div>
                          <textarea
                            value={feedbackMessage}
                            onChange={(e) => setFeedbackMessage(e.target.value)}
                            placeholder="Describe the issue..."
                            className="w-full text-xs px-2 py-1.5 border border-gray-300 dark:border-dark-border rounded-lg dark:bg-dark-surface dark:text-dark-text resize-none"
                            rows={2}
                          />
                          <button
                            disabled={!feedbackMessage.trim() || feedbackSending}
                            onClick={async () => {
                              if (!card || !feedbackMessage.trim()) return
                              setFeedbackSending(true)
                              try {
                                await feedbackApi.submit(card.id, feedbackCategory, feedbackMessage)
                                toast.success('Feedback submitted')
                                setShowFeedback(false)
                                setFeedbackMessage('')
                                setFeedbackCategory('other')
                              } catch {
                                toast.error('Failed to submit feedback')
                              } finally {
                                setFeedbackSending(false)
                              }
                            }}
                            className="w-full flex items-center justify-center gap-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg py-1.5 transition-colors"
                          >
                            <Send className="h-3 w-3" />
                            {feedbackSending ? 'Sending...' : 'Submit Feedback'}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Variants Section - Collapsible */}
                    <div className="mt-3 border-t dark:border-dark-border pt-2">
                      <button
                        onClick={() => {
                          const newExpanded = !variantsExpanded
                          setVariantsExpanded(newExpanded)
                          if (newExpanded) {
                            setUserExpandedVariants(true)
                          }
                        }}
                        className="w-full flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-dark-border -mx-2 px-2 py-1 rounded-lg transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <Layers className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                          <span className="text-sm font-semibold text-gray-900 dark:text-dark-text">
                            Card Variants {variants.length > 0 && `(${variants.length})`}
                          </span>
                        </div>
                        {variantsExpanded ? (
                          <ChevronUp className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                        )}
                      </button>

                      {variantsExpanded && (
                        <div className="mt-3">
                          {loadingVariants ? (
                            <div className="flex justify-center py-3">
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                            </div>
                          ) : variants.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {variants.map((variant) => {
                                const isJPVariant = variant.language === 'ja' || variant.id?.endsWith('-JP') || variant.id?.includes('-JP_')
                                return (
                                <div
                                  key={variant.id}
                                  onClick={() => onCardChange?.(variant)}
                                  className={`relative cursor-pointer rounded-lg transition-all hover:scale-105 ${
                                    variant.id === card.id
                                      ? 'ring-2 ring-blue-500 ring-offset-1'
                                      : 'hover:ring-2 hover:ring-gray-300'
                                  }`}
                                  title={`${variant.name} (${variant.number})${isJPVariant ? ' [JP]' : ' [EN]'}`}
                                >
                                  <img
                                    src={proxyImageUrl(variant.image_url)}
                                    alt={variant.name}
                                    className="w-16 h-[88px] object-cover rounded-lg shadow-md"
                                  />
                                  <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-white dark:bg-dark-surface shadow-sm border border-gray-200 dark:border-dark-border flex items-center justify-center text-[8px] leading-none" title={isJPVariant ? 'Japanese' : 'English'}>
                                    {isJPVariant ? '\u{1F1EF}\u{1F1F5}' : '\u{1F1FA}\u{1F1F8}'}
                                  </span>
                                </div>
                                )
                              })}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500 dark:text-dark-muted text-center py-2">No variants found</p>
                          )}
                        </div>
                      )}
                    </div>

                  </div>
                </div>
    </>
  )

  // Inline (page) mode: render content directly
  if (inline) {
    return (
      <>
        <div className="max-w-5xl mx-auto">
          <div className="bg-white dark:bg-dark-surface rounded-lg shadow-xl overflow-hidden">
            {cardContent}
          </div>
        </div>

        {/* Create Collection Modal */}
    <Transition appear show={showCreateModal} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => setShowCreateModal(false)}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-dark-card p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900 dark:text-dark-text mb-4"
                >
                  Create New Collection
                </Dialog.Title>

                <form onSubmit={handleCreateCollection} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                      Collection Name *
                    </label>
                    <input
                      type="text"
                      value={newCollectionName}
                      onChange={(e) => setNewCollectionName(e.target.value)}
                      placeholder="e.g., For Trade, Investment, Personal"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-dark-surface dark:text-dark-text"
                      required
                      autoFocus
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                      Description (Optional)
                    </label>
                    <textarea
                      value={newCollectionDescription}
                      onChange={(e) => setNewCollectionDescription(e.target.value)}
                      placeholder="Add notes about this collection..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-dark-surface dark:text-dark-text"
                      rows={3}
                    />
                  </div>

                  <div className="flex gap-3 justify-end">
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-text bg-gray-100 dark:bg-dark-surface hover:bg-gray-200 dark:hover:bg-dark-border rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={createCollectionMutation.isPending || !newCollectionName.trim()}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {createCollectionMutation.isPending ? 'Creating...' : 'Create Collection'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
        {isAdmin && card && (
          <AdminCardEditModal
            cardId={card.id}
            open={adminEditOpen}
            onClose={() => setAdminEditOpen(false)}
            onChanged={() => {
              queryClient.invalidateQueries({ queryKey: ['card', card.id] })
              queryClient.invalidateQueries({ queryKey: ['allCards'] })
              queryClient.invalidateQueries({ queryKey: ['cards'] })
            }}
          />
        )}
    </>
    )
  }

  // Modal mode: wrap content in Dialog/Transition
  return (
    <>
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-full sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-full sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-t-2xl sm:rounded-lg bg-white dark:bg-dark-surface text-left shadow-xl transition-all w-full sm:max-w-5xl max-h-[90vh] sm:max-h-[85vh] overflow-y-auto">
                {cardContent}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>

    {/* Create Collection Modal */}
    <Transition appear show={showCreateModal} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => setShowCreateModal(false)}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-dark-card p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900 dark:text-dark-text mb-4"
                >
                  Create New Collection
                </Dialog.Title>

                <form onSubmit={handleCreateCollection} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                      Collection Name *
                    </label>
                    <input
                      type="text"
                      value={newCollectionName}
                      onChange={(e) => setNewCollectionName(e.target.value)}
                      placeholder="e.g., For Trade, Investment, Personal"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-dark-surface dark:text-dark-text"
                      required
                      autoFocus
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                      Description (Optional)
                    </label>
                    <textarea
                      value={newCollectionDescription}
                      onChange={(e) => setNewCollectionDescription(e.target.value)}
                      placeholder="Add notes about this collection..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-dark-surface dark:text-dark-text"
                      rows={3}
                    />
                  </div>

                  <div className="flex gap-3 justify-end">
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-text bg-gray-100 dark:bg-dark-surface hover:bg-gray-200 dark:hover:bg-dark-border rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={createCollectionMutation.isPending || !newCollectionName.trim()}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {createCollectionMutation.isPending ? 'Creating...' : 'Create Collection'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
    {isAdmin && card && (
      <AdminCardEditModal
        cardId={card.id}
        open={adminEditOpen}
        onClose={() => setAdminEditOpen(false)}
        onChanged={() => {
          queryClient.invalidateQueries({ queryKey: ['card', card.id] })
          queryClient.invalidateQueries({ queryKey: ['allCards'] })
          queryClient.invalidateQueries({ queryKey: ['cards'] })
        }}
      />
    )}
    </>
  )
}